import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, fromEvent, interval, startWith } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { Shared } from './shared';
import { LoadingService } from './core/services/loading.service';
import { NotificationsService } from './core/services/notifications.service';
import { ExecutionStatus } from './proxy/models/common';
import { MainLoadingComponent } from './shared/components/main-loading/main-loading.component';
import { PromptUpdateService } from './core/services/prompt-update.service';

import { NotificationsPanelService } from './core/services/notifications-panel.service';
import { HolidayNotificationsService } from './core/services/holiday-notifications.service';
import { NotificationsPanelComponent } from './shared/components/notifications-panel/notifications-panel-component';
import { ShortcutService } from './core/services/shortcut-binding.service';
import { getShortcutList, getShortcutPrefix } from './core/global/constants/page-shortcut';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { SessionPollService } from './auth/session-poll.service';
import { SignedInSentinel } from './core/global/constants/constant';
import { ChordIndicatorComponent } from './shared/components/chord-indicator/chord-indicator.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    Shared,
    MainLoadingComponent,
    NotificationsPanelComponent,
    ChordIndicatorComponent,
  ],
  providers: [
    PromptUpdateService,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly signedInSentinel = SignedInSentinel;
  public readonly oidc = inject(OidcSecurityService);
  private readonly sessionPoll = inject(SessionPollService);

  private destroyRef = inject(DestroyRef);
  private promptUpdateService = inject(PromptUpdateService);
  protected shortcutService = inject(ShortcutService);
  protected readonly shortcutPrefix = getShortcutPrefix();
  protected readonly shortcuts = getShortcutList();
  protected readonly shortcutRows = [
    this.shortcuts.slice(0, Math.ceil(this.shortcuts.length / 2)),
    this.shortcuts.slice(Math.ceil(this.shortcuts.length / 2)),
  ];
  private router = inject(Router);
  private modalService = inject(NgbModal);
  public notificationsService = inject(NotificationsService);

  public loadingService = inject(LoadingService);
  public notificationsPanelService = inject(NotificationsPanelService);
  public holidayNotificationsService = inject(HolidayNotificationsService);

  constructor() {
    fromEvent<KeyboardEvent>(window, 'keydown')
      .pipe(
        switchMap(event => this.oidc.isAuthenticated().pipe(
          take(1),
          map(isAuthenticated => ({event, isAuthenticated}))
        )),
        filter(({isAuthenticated}) => isAuthenticated),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(({ event }) => this.shortcutService.handleKeydown(event));

    this.closeAllDialogs();
  }

  public async ngOnInit(): Promise<void> {
    const wasSignedIn = sessionStorage.getItem(this.signedInSentinel) === 'true';
    this.oidc.checkAuth().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({isAuthenticated, userData}) => {
      this.getHolidayNotifications();

      if (isAuthenticated && userData) {
        sessionStorage.setItem(this.signedInSentinel, 'true');
        this.sessionPoll.start();
        this.getNotifications();
        return;
      }

      this.sessionPoll.stop();

      const onLogoutRoute = window.location.pathname.startsWith('/auth/logout');

      if (wasSignedIn && !onLogoutRoute) {
        this.oidc.authorize();
      } else {
        sessionStorage.removeItem(this.signedInSentinel);
      }
    });
  }

  private getNotifications(): void {
    interval(15 * 60 * 1000)
      .pipe(
        startWith(0),
        switchMap(() => this.notificationsService.getUnreadNotifications()),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((res) => {
      if (res?.result?.status === ExecutionStatus.success) {
        this.notificationsService.setNotifications(res.abeList);
      }
    });
  }

  private getHolidayNotifications(): void {
    this.holidayNotificationsService.getNotifications();
  }

  private closeAllDialogs(): void {
    this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.modalService.dismissAll();
      }
    });
  }
}
